import { useState, useEffect, useCallback } from "react";
import api from "../../services/api";
import {
  AreaChart, Area,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
ReferenceLine, ResponsiveContainer, Cell,
} from "recharts";

// ── Constants ────────────────────────────────────────────────────
const PROJECT_TYPES = [
  { id:"acquisition",      icon:"🏢", label:"Acquisition",       color:"#3b82f6" },
  { id:"recrutement",      icon:"👥", label:"Recrutement",        color:"#8b5cf6" },
  { id:"nouveau_marché",   icon:"🌍", label:"Nouveau marché",     color:"#1a9e72" },
  { id:"lancement_produit",icon:"🚀", label:"Lancement produit",  color:"#f59e0b" },
  { id:"saas_mrr",         icon:"☁️", label:"SaaS / MRR",         color:"#ec4899" },
  { id:"export",           icon:"📦", label:"Export",             color:"#0ea5e9" },
  { id:"autre",            icon:"📌", label:"Autre",              color:"#6b7280" },
];

const EMPTY_FORM = {
  name:"", project_type:"acquisition", start_date:"", investment_amount:"",
  expected_revenue_y1:"", expected_revenue_y2:"", expected_revenue_y3:"",
  monthly_cost:"", notes:"",
  // SaaS extras
  saas_mrr_start:"", saas_growth_rate:"", saas_churn_rate:"", saas_arpu:"",
};

// ── Helpers ──────────────────────────────────────────────────────
function typeInfo(id) { return PROJECT_TYPES.find(t=>t.id===id)||PROJECT_TYPES[PROJECT_TYPES.length-1]; }
function fmt(n)  { return (n||0).toLocaleString("fr-FR",{maximumFractionDigits:0})+" €"; }
function fmtK(n) { return (Math.abs(n||0)/1000).toFixed(0)+"k€"; }

function buildCashflow(proj) {
  const invest  = proj.investment_amount || 0;
  const revs    = [proj.expected_revenue_y1||0, proj.expected_revenue_y2||0, proj.expected_revenue_y3||0];
  const monthly = proj.monthly_cost || 0;
  return Array.from({length:36},(_,mo)=>{
    const yr   = mo<12?0:mo<24?1:2;
    const rev  = revs[yr]/12;
    const cost = monthly+(mo===0?invest:0);
    return {mo:mo+1,label:`M${mo+1}`,rev,cost,net:rev-cost};
  });
}

function buildCumulative(cf) {
  let cum = 0;
  return cf.map(p=>{ cum+=p.net; return {...p,cum}; });
}

// MRR simulation: ARR = MRR*12*(1+g-c)^t
function buildMRRCashflow(proj) {
  const mrrStart  = parseFloat(proj.saas_mrr_start||0);
  const growthMo  = parseFloat(proj.saas_growth_rate||5)/100/12;
  const churnMo   = parseFloat(proj.saas_churn_rate||2)/100/12;
  const invest    = proj.investment_amount||0;
  const monthly   = proj.monthly_cost||0;

  return Array.from({length:36},(_,mo)=>{
    const mrr  = mrrStart * Math.pow(1+growthMo-churnMo, mo);
    const cost = monthly+(mo===0?invest:0);
    return {mo:mo+1,label:`M${mo+1}`,rev:mrr,cost,net:mrr-cost,mrr};
  });
}

// Sensitivity: apply a factor to revenues
function buildSensitivity(proj) {
  const scenarios = [
    {label:"Pessimiste",factor:0.7,color:"#ef4444"},
    {label:"Central",   factor:1.0,color:"#3b82f6"},
    {label:"Optimiste", factor:1.3,color:"#16a34a"},
  ];
  return scenarios.map(sc=>{
    const r1 = (proj.expected_revenue_y1||0)*sc.factor;
    const r2 = (proj.expected_revenue_y2||0)*sc.factor;
    const r3 = (proj.expected_revenue_y3||0)*sc.factor;
    const inv = proj.investment_amount||0;
    const mon = (proj.monthly_cost||0)*12;
    const totalRev  = r1+r2+r3;
    const totalCost = inv+mon*3;
    const roi = totalCost>0 ? ((totalRev-totalCost)/totalCost*100) : null;
    // Payback: month where cumulative crosses 0
    const cf = buildCashflow({...proj,expected_revenue_y1:r1,expected_revenue_y2:r2,expected_revenue_y3:r3});
    const cum = buildCumulative(cf);
    const pb  = cum.findIndex(p=>p.cum>=0);
    return {...sc,roi,payback:pb>=0?pb+1:null,totalRev,totalCost};
  });
}

// ── Sub-components ───────────────────────────────────────────────
function ProjectCard({proj,onEdit,onDelete,onSelect,isActive}) {
  const info = typeInfo(proj.project_type);
  return (
    <div onClick={onSelect} style={{
      background:"var(--card-bg,#fff)",
      border:`2px solid ${isActive?info.color:"var(--border-color,#e2e8f0)"}`,
      borderRadius:12,padding:"16px 18px",cursor:"pointer",transition:"border-color .15s",
    }}>
      <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
        <div style={{width:40,height:40,borderRadius:10,background:`${info.color}18`,
          display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>
          {info.icon}
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:14,fontWeight:700,marginBottom:4}}>{proj.name}</div>
          <span style={{fontSize:11,padding:"2px 8px",borderRadius:8,
            background:`${info.color}18`,color:info.color,fontWeight:600}}>{info.label}</span>
          {proj.start_date && (
            <span style={{fontSize:11,color:"var(--text-muted)",marginLeft:8}}>
              Démarrage : {new Date(proj.start_date).toLocaleDateString("fr-FR")}
            </span>
          )}
        </div>
        <div onClick={e=>e.stopPropagation()} style={{display:"flex",gap:6,flexShrink:0}}>
          <button onClick={onEdit} style={{padding:"3px 9px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:11,cursor:"pointer",background:"#f9fafb"}}>Modifier</button>
          <button onClick={onDelete} style={{padding:"3px 9px",border:"1px solid #fecaca",borderRadius:6,fontSize:11,cursor:"pointer",background:"#fef2f2",color:"#dc2626"}}>Suppr.</button>
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginTop:14,
        paddingTop:14,borderTop:"1px solid #f3f4f6"}}>
        {[
          {label:"Investissement", val:fmt(proj.investment_amount)},
          {label:"CA Y1",          val:fmt(proj.expected_revenue_y1)},
          {label:"ROI 3 ans",      val:proj.roi_percent!=null?proj.roi_percent.toFixed(1)+"%":"–",
            color:proj.roi_percent>=0?"#16a34a":"#dc2626"},
          {label:"Payback",        val:proj.payback_months!=null?`${proj.payback_months} mois`:"–"},
        ].map(({label,val,color})=>(
          <div key={label} style={{textAlign:"center"}}>
            <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:2}}>{label}</div>
            <div style={{fontSize:13,fontWeight:700,color:color||"var(--text,#1f2937)"}}>{val}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────
export default function ProjectsTab({currentCompanyId}) {
  const [projects,  setProjects]  = useState([]);
  const [selected,  setSelected]  = useState(null);
  const [showForm,  setShowForm]  = useState(false);
  const [editProj,  setEditProj]  = useState(null);
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [saving,    setSaving]    = useState(false);
  const [msg,       setMsg]       = useState("");
  const [detailTab, setDetailTab] = useState("cashflow"); // cashflow | sensitivity | saas

  const load = useCallback(()=>{
    if(!currentCompanyId) return;
    api.budget.projects(currentCompanyId).then(setProjects).catch(console.error);
  },[currentCompanyId]);

  useEffect(()=>{load();},[load]);

  const openCreate = () => { setEditProj(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit   = (p) => {
    setEditProj(p);
    setForm({
      ...EMPTY_FORM,...p,
      investment_amount:String(p.investment_amount||""),
      expected_revenue_y1:String(p.expected_revenue_y1||""),
      expected_revenue_y2:String(p.expected_revenue_y2||""),
      expected_revenue_y3:String(p.expected_revenue_y3||""),
      monthly_cost:String(p.monthly_cost||""),
    });
    setShowForm(true);
  };

  const save = async()=>{
    if(!form.name) return;
    setSaving(true);
    const payload = {
      ...form, company_id:currentCompanyId,
      investment_amount:   parseFloat(form.investment_amount)||0,
      expected_revenue_y1: parseFloat(form.expected_revenue_y1)||0,
      expected_revenue_y2: parseFloat(form.expected_revenue_y2)||0,
      expected_revenue_y3: parseFloat(form.expected_revenue_y3)||0,
      monthly_cost:        parseFloat(form.monthly_cost)||0,
    };
    try {
      if(editProj) await api.budget.updateProject(editProj.id,payload);
      else         await api.budget.createProject(payload);
      setMsg("✓ Projet enregistré");
      setShowForm(false); load();
    } catch(e){ setMsg("Erreur : "+e.message); }
    finally { setSaving(false); setTimeout(()=>setMsg(""),3000); }
  };

  const del = async(p)=>{
    if(!window.confirm(`Supprimer le projet "${p.name}" ?`)) return;
    try{ await api.budget.deleteProject(p.id); load(); if(selected?.id===p.id) setSelected(null); }
    catch(e){ setMsg("Erreur : "+e.message); }
  };

  // Computed data for selected project
  const isSaaS     = selected?.project_type==="saas_mrr";
  const cf         = selected ? (isSaaS?buildMRRCashflow(selected):buildCashflow(selected)) : [];
  const cumulative = buildCumulative(cf);
  const sensitivity= selected ? buildSensitivity(selected) : [];
  const info       = selected ? typeInfo(selected.project_type) : null;

  // Portfolio summary
  const totalInvested  = projects.reduce((s,p)=>s+(p.investment_amount||0),0);
  const avgROI         = projects.filter(p=>p.roi_percent!=null).reduce((s,p,_,a)=>s+(p.roi_percent||0)/a.length,0);
  const avgPayback     = projects.filter(p=>p.payback_months!=null).reduce((s,p,_,a)=>s+(p.payback_months||0)/a.length,0);

  const inputSt = {width:"100%",padding:"8px 11px",border:"1px solid var(--border-color,#e2e8f0)",
    borderRadius:8,fontSize:13,background:"var(--input-bg,#fff)",color:"var(--text,#1f2937)",boxSizing:"border-box"};
  const cardSt  = {background:"var(--card-bg,#fff)",border:"1px solid var(--border-color,#e2e8f0)",borderRadius:12,padding:18};

  return (
    <div>
      {/* Portfolio summary */}
      {projects.length>0 && (
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
          {[
            {label:"Projets actifs",  val:projects.length,             unit:"",       color:"#3b82f6"},
            {label:"Investissement total",val:fmt(totalInvested),      unit:"",       color:"#ef4444"},
            {label:"ROI moyen 3 ans", val:avgROI.toFixed(1)+"%",       unit:"",       color:avgROI>=0?"#16a34a":"#dc2626"},
            {label:"Payback moyen",   val:projects.some(p=>p.payback_months)?Math.round(avgPayback)+" mois":"N/A",
              unit:"",color:"#f59e0b"},
          ].map(({label,val,color})=>(
            <div key={label} style={{...cardSt,textAlign:"center"}}>
              <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:4}}>{label}</div>
              <div style={{fontSize:18,fontWeight:700,color}}>{val}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{display:"flex",gap:20}}>
        {/* Left — project list */}
        <div style={{flex:1,display:"flex",flexDirection:"column",gap:12}}>
          <div style={{display:"flex",gap:10,alignItems:"center"}}>
            <div style={{flex:1,fontSize:13,color:"var(--text-muted)"}}>{projects.length} projet(s)</div>
            {msg && <span style={{fontSize:12,color:msg.startsWith("✓")?"#16a34a":"#dc2626"}}>{msg}</span>}
            <button onClick={openCreate} style={{padding:"7px 14px",background:"#1a9e72",color:"#fff",
              border:"none",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer"}}>
              + Nouveau projet
            </button>
          </div>

          {projects.length===0 ? (
            <div style={{textAlign:"center",padding:80,color:"var(--text-muted)"}}>
              <div style={{fontSize:40,marginBottom:12}}>🚀</div>
              <div style={{fontSize:15,fontWeight:600}}>Aucun projet</div>
              <div style={{fontSize:13,marginTop:6}}>Créez des scénarios projets pour simuler leur impact financier</div>
            </div>
          ) : (
            projects.map(p=>(
              <ProjectCard key={p.id} proj={p} isActive={selected?.id===p.id}
                onSelect={()=>setSelected(s=>s?.id===p.id?null:p)}
                onEdit={()=>openEdit(p)}
                onDelete={()=>del(p)}
              />
            ))
          )}
        </div>

        {/* Right — project detail */}
        {selected && info && (
          <div style={{width:400,display:"flex",flexDirection:"column",gap:14}}>
            {/* Header card */}
            <div style={{...cardSt,border:`2px solid ${info.color}`}}>
              <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:16}}>
                <span style={{fontSize:28}}>{info.icon}</span>
                <div>
                  <div style={{fontSize:15,fontWeight:700}}>{selected.name}</div>
                  <div style={{fontSize:11,color:info.color,fontWeight:600}}>{info.label}</div>
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                {[
                  {label:"Investissement",   val:fmt(selected.investment_amount),                          color:"#dc2626"},
                  {label:"Coût mensuel",      val:fmt(selected.monthly_cost)+"/m",                          color:"#f59e0b"},
                  {label:"CA Y1",             val:fmt(selected.expected_revenue_y1)},
                  {label:"CA Y2",             val:fmt(selected.expected_revenue_y2)},
                  {label:"CA Y3",             val:fmt(selected.expected_revenue_y3)},
                  {label:"CA total 3 ans",    val:fmt((selected.expected_revenue_y1||0)+(selected.expected_revenue_y2||0)+(selected.expected_revenue_y3||0)),color:"#3b82f6"},
                  {label:"ROI 3 ans",         val:selected.roi_percent!=null?selected.roi_percent.toFixed(1)+"%":"–",
                    color:selected.roi_percent>=0?"#16a34a":"#dc2626"},
                  {label:"Payback",           val:selected.payback_months!=null?`${selected.payback_months} mois`:"Non atteint",
                    color:selected.payback_months?"#16a34a":"#6b7280"},
                ].map(({label,val,color})=>(
                  <div key={label} style={{background:"#f9fafb",borderRadius:8,padding:"10px 12px"}}>
                    <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:3}}>{label}</div>
                    <div style={{fontSize:13,fontWeight:700,color:color||"var(--text,#1f2937)"}}>{val}</div>
                  </div>
                ))}
              </div>
              {selected.notes && (
                <div style={{fontSize:11,color:"var(--text-muted)",borderTop:"1px solid #f3f4f6",
                  paddingTop:10,marginTop:12}}>{selected.notes}</div>
              )}
            </div>

            {/* Detail tabs */}
            <div style={{display:"flex",gap:0,borderBottom:"2px solid var(--border-color,#e2e8f0)"}}>
              {[
                {id:"cashflow", label:"Cash-flow"},
                {id:"sensitivity", label:"Sensibilité"},
                ...(isSaaS?[{id:"saas",label:"MRR / SaaS"}]:[]),
              ].map(t=>(
                <button key={t.id} onClick={()=>setDetailTab(t.id)} style={{
                  padding:"7px 14px",fontSize:12,fontWeight:detailTab===t.id?700:500,
                  cursor:"pointer",border:"none",background:"transparent",
                  color:detailTab===t.id?"var(--g)":"var(--text-muted)",
                  borderBottom:`2px solid ${detailTab===t.id?"var(--g)":"transparent"}`,
                  marginBottom:-2,
                }}>{t.label}</button>
              ))}
            </div>

            {/* Cash-flow chart */}
            {detailTab==="cashflow" && (
              <>
                <div style={cardSt}>
                  <div style={{fontSize:13,fontWeight:700,marginBottom:12}}>
                    Cash-flow cumulé sur 36 mois
                    {isSaaS && <span style={{fontSize:10,color:"#ec4899",marginLeft:8}}>mode MRR</span>}
                  </div>
                  <ResponsiveContainer width="100%" height={180}>
                    <AreaChart data={cumulative}>
                      <defs>
                        <linearGradient id="cfGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor={info.color} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={info.color} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6"/>
                      <XAxis dataKey="mo" tick={{fontSize:9}} tickFormatter={v=>`M${v}`} interval={5}/>
                      <YAxis tick={{fontSize:9}} tickFormatter={fmtK}/>
                      <Tooltip formatter={(v)=>[fmt(v),"Cash-flow cumulé"]} labelFormatter={v=>`Mois ${v}`}/>
                      <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="4 2"/>
                      <Area type="monotone" dataKey="cum" stroke={info.color} fill="url(#cfGrad)" strokeWidth={2} dot={false}/>
                    </AreaChart>
                  </ResponsiveContainer>
                  {selected.payback_months && (
                    <div style={{fontSize:11,textAlign:"center",color:"#16a34a",marginTop:6,fontWeight:600}}>
                      Point mort atteint au mois {selected.payback_months}
                    </div>
                  )}
                </div>

                <div style={cardSt}>
                  <div style={{fontSize:13,fontWeight:700,marginBottom:10}}>P&L par année</div>
                  {[1,2,3].map(yr=>{
                    const slice   = cumulative.slice((yr-1)*12,yr*12);
                    const totalRev  = slice.reduce((s,m)=>s+m.rev,0);
                    const totalCost = slice.reduce((s,m)=>s+m.cost,0);
                    return (
                      <div key={yr} style={{display:"flex",justifyContent:"space-between",
                        padding:"6px 0",borderBottom:"1px solid #f3f4f6",fontSize:12}}>
                        <span style={{color:"var(--text-muted)",fontWeight:600}}>Année {yr}</span>
                        <span>CA : {fmt(totalRev)}</span>
                        <span>Coûts : {fmt(totalCost)}</span>
                        <span style={{fontWeight:700,color:totalRev-totalCost>=0?"#16a34a":"#dc2626"}}>
                          Net : {fmt(totalRev-totalCost)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Sensitivity table */}
            {detailTab==="sensitivity" && (
              <div style={cardSt}>
                <div style={{fontSize:13,fontWeight:700,marginBottom:4}}>Analyse de sensibilité</div>
                <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:14}}>
                  Impact de ±30% sur les revenus prévus
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {sensitivity.map(sc=>(
                    <div key={sc.label} style={{border:`1px solid ${sc.color}`,borderRadius:10,padding:14,
                      background:`${sc.color}08`}}>
                      <div style={{fontSize:13,fontWeight:700,color:sc.color,marginBottom:8}}>{sc.label}</div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                        {[
                          {label:"CA total 3 ans", val:fmt(sc.totalRev)},
                          {label:"Coûts totaux",   val:fmt(sc.totalCost)},
                          {label:"ROI",            val:sc.roi!=null?sc.roi.toFixed(1)+"%":"—",
                            color:sc.roi>=0?"#16a34a":"#dc2626"},
                          {label:"Payback",        val:sc.payback?`${sc.payback} mois`:"Non atteint",
                            color:sc.payback?"#16a34a":"#6b7280"},
                        ].map(({label,val,color})=>(
                          <div key={label}>
                            <div style={{fontSize:10,color:"var(--text-muted)"}}>{label}</div>
                            <div style={{fontSize:13,fontWeight:700,color:color||"var(--text)"}}>{val}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {/* Bar chart for ROI comparison */}
                <div style={{marginTop:14}}>
                  <ResponsiveContainer width="100%" height={140}>
                    <BarChart data={sensitivity.map(sc=>({name:sc.label,roi:sc.roi||0}))}
                      margin={{top:4,right:8,bottom:0,left:0}}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6"/>
                      <XAxis dataKey="name" tick={{fontSize:10}}/>
                      <YAxis tick={{fontSize:10}} tickFormatter={v=>`${v}%`}/>
                      <Tooltip formatter={v=>`${v.toFixed(1)}%`}/>
                      <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="3 2"/>
                      <Bar dataKey="roi" name="ROI" radius={[4,4,0,0]}>
                        {sensitivity.map((sc,i)=><Cell key={i} fill={sc.color}/>)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* SaaS / MRR detail */}
            {detailTab==="saas" && isSaaS && (
              <div style={cardSt}>
                <div style={{fontSize:13,fontWeight:700,marginBottom:14}}>Simulation SaaS — MRR</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
                  {[
                    {label:"MRR initial",    val:fmt(parseFloat(selected.saas_mrr_start||0)),   color:"#ec4899"},
                    {label:"ARR initial",    val:fmt(parseFloat(selected.saas_mrr_start||0)*12),color:"#8b5cf6"},
                    {label:"Taux croissance",val:(selected.saas_growth_rate||0)+"%/mois",        color:"#16a34a"},
                    {label:"Taux churn",     val:(selected.saas_churn_rate||0)+"%/mois",         color:"#ef4444"},
                    {label:"ARPU",           val:fmt(parseFloat(selected.saas_arpu||0)),         color:"#3b82f6"},
                    {label:"MRR à 36 mois",  val:fmt(buildMRRCashflow(selected)[35]?.mrr||0),    color:"#ec4899"},
                  ].map(({label,val,color})=>(
                    <div key={label} style={{background:"#f9fafb",borderRadius:8,padding:"10px 12px"}}>
                      <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:3}}>{label}</div>
                      <div style={{fontSize:13,fontWeight:700,color}}>{val}</div>
                    </div>
                  ))}
                </div>

                <div style={{fontSize:12,fontWeight:600,marginBottom:8,color:"var(--text-muted)"}}>MRR sur 36 mois</div>
                <ResponsiveContainer width="100%" height={160}>
                  <AreaChart data={buildMRRCashflow(selected)}>
                    <defs>
                      <linearGradient id="mrrGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#ec4899" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#ec4899" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6"/>
                    <XAxis dataKey="mo" tick={{fontSize:9}} tickFormatter={v=>`M${v}`} interval={5}/>
                    <YAxis tick={{fontSize:9}} tickFormatter={fmtK}/>
                    <Tooltip formatter={(v,n)=>[fmt(v),n]} labelFormatter={v=>`Mois ${v}`}/>
                    <Area type="monotone" dataKey="mrr" name="MRR"    stroke="#ec4899" fill="url(#mrrGrad)" strokeWidth={2} dot={false}/>
                    <Area type="monotone" dataKey="cost" name="Coûts" stroke="#f59e0b" fill="none" strokeWidth={1.5} dot={false} strokeDasharray="4 2"/>
                  </AreaChart>
                </ResponsiveContainer>

                <div style={{marginTop:12,fontSize:11,color:"var(--text-muted)"}}>
                  <strong>LTV estimé</strong> (si ARPU & churn renseignés) :{" "}
                  {selected.saas_churn_rate>0
                    ? fmt(parseFloat(selected.saas_arpu||0)/(parseFloat(selected.saas_churn_rate||0)/100))
                    : "—"}
                  {" "}· <strong>CAC</strong> :{" "}
                  {fmt(parseFloat(selected.investment_amount||0)/(buildMRRCashflow(selected)[35]?.mrr/parseFloat(selected.saas_arpu||1)||1))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create / Edit modal */}
      {showForm && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",display:"flex",
          alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{background:"var(--card-bg,#fff)",borderRadius:14,padding:28,
            width:560,maxHeight:"90vh",overflowY:"auto",boxShadow:"0 20px 60px rgba(0,0,0,.3)"}}>
            <h3 style={{margin:"0 0 18px",fontSize:16,fontWeight:700}}>
              {editProj?"Modifier le projet":"Nouveau projet"}
            </h3>

            {/* Type selector */}
            <div style={{marginBottom:14}}>
              <label style={{display:"block",fontSize:12,color:"var(--text-muted)",marginBottom:6}}>Type de projet</label>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:6}}>
                {PROJECT_TYPES.map(t=>(
                  <div key={t.id} onClick={()=>setForm(f=>({...f,project_type:t.id}))} style={{
                    padding:"8px 6px",textAlign:"center",borderRadius:8,cursor:"pointer",
                    border:`2px solid ${form.project_type===t.id?t.color:"#e5e7eb"}`,
                    background:form.project_type===t.id?`${t.color}12`:"transparent",transition:"all .12s",
                  }}>
                    <div style={{fontSize:18}}>{t.icon}</div>
                    <div style={{fontSize:9,color:form.project_type===t.id?t.color:"var(--text-muted)",
                      fontWeight:600,marginTop:2}}>{t.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div>
                <label style={{display:"block",fontSize:12,color:"var(--text-muted)",marginBottom:4}}>Nom du projet *</label>
                <input style={inputSt} value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}
                  placeholder="Ex : Ouverture bureau Lyon"/>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <div>
                  <label style={{display:"block",fontSize:12,color:"var(--text-muted)",marginBottom:4}}>Date de démarrage</label>
                  <input type="date" style={inputSt} value={form.start_date}
                    onChange={e=>setForm(f=>({...f,start_date:e.target.value}))}/>
                </div>
                <div>
                  <label style={{display:"block",fontSize:12,color:"var(--text-muted)",marginBottom:4}}>Investissement initial (€)</label>
                  <input type="number" style={inputSt} value={form.investment_amount}
                    onChange={e=>setForm(f=>({...f,investment_amount:e.target.value}))} placeholder="0"/>
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
                {[["CA attendu Y1 (€)","expected_revenue_y1"],["CA attendu Y2 (€)","expected_revenue_y2"],["CA attendu Y3 (€)","expected_revenue_y3"]].map(([lbl,key])=>(
                  <div key={key}>
                    <label style={{display:"block",fontSize:12,color:"var(--text-muted)",marginBottom:4}}>{lbl}</label>
                    <input type="number" style={inputSt} value={form[key]}
                      onChange={e=>setForm(f=>({...f,[key]:e.target.value}))} placeholder="0"/>
                  </div>
                ))}
              </div>
              <div>
                <label style={{display:"block",fontSize:12,color:"var(--text-muted)",marginBottom:4}}>Coût mensuel récurrent (€/mois)</label>
                <input type="number" style={inputSt} value={form.monthly_cost}
                  onChange={e=>setForm(f=>({...f,monthly_cost:e.target.value}))} placeholder="0"/>
              </div>

              {/* SaaS extras */}
              {form.project_type==="saas_mrr" && (
                <div style={{background:"#fdf2f8",border:"1px solid #fbcfe8",borderRadius:10,padding:14}}>
                  <div style={{fontSize:12,fontWeight:700,color:"#ec4899",marginBottom:10}}>☁️ Paramètres SaaS / MRR</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                    {[
                      ["MRR initial (€)","saas_mrr_start","0"],
                      ["ARPU (€/client)","saas_arpu","0"],
                      ["Croissance mensuelle (%)","saas_growth_rate","5"],
                      ["Churn mensuel (%)","saas_churn_rate","2"],
                    ].map(([lbl,key,ph])=>(
                      <div key={key}>
                        <label style={{display:"block",fontSize:11,color:"var(--text-muted)",marginBottom:3}}>{lbl}</label>
                        <input type="number" step="0.1" style={{...inputSt,fontSize:12}} value={form[key]||""}
                          onChange={e=>setForm(f=>({...f,[key]:e.target.value}))} placeholder={ph}/>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label style={{display:"block",fontSize:12,color:"var(--text-muted)",marginBottom:4}}>Notes</label>
                <textarea style={{...inputSt,height:64,resize:"vertical"}} value={form.notes}
                  onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/>
              </div>
            </div>

            <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:20}}>
              <button onClick={()=>setShowForm(false)} style={{padding:"8px 16px",border:"1px solid #e5e7eb",
                borderRadius:8,background:"transparent",cursor:"pointer",fontSize:13}}>Annuler</button>
              <button onClick={save} disabled={saving||!form.name} style={{padding:"8px 16px",
                background:form.name?"#1a9e72":"#e5e7eb",color:form.name?"#fff":"#9ca3af",
                border:"none",borderRadius:8,cursor:form.name?"pointer":"not-allowed",fontSize:13,fontWeight:600}}>
                {saving?"Enregistrement…":editProj?"Mettre à jour":"Créer le projet"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
